import { Alert, AlertTitle, Box, Button } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import { isOnboardingRoute } from './route';
import { useOnboarding } from './useOnboarding';

/**
 * What an established tenant still owes, said out loud instead of enforced.
 *
 * The counterpart to the gate in `RequireAuth`. Before a tenant's first
 * approval an unfinished profile holds them at the wizard; afterwards the same
 * unfinished profile shows this and nothing else, because an approved company
 * losing their jobs board over an empty rate box is out of proportion to the
 * mistake — and arrives with no explanation, since the wizard they land on
 * says nothing about which box it wants.
 *
 * Deliberately not dismissible. It is the only thing standing between a
 * provider and being alerted to work they have published no price for, and a
 * banner that can be waved away is one that will be.
 */
export default function OnboardingBanner() {
  const { status } = useOnboarding();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  // Nothing outstanding, or outstanding *and* blocking — in which case the
  // gate has already sent them to the wizard and saying it twice is noise.
  if (!status || status.complete || status.blocked) return null;

  // The wizard and the pages it links to say all of this in more detail.
  if (isOnboardingRoute(pathname)) return null;

  const fields = status.outstanding.flatMap((step) => step.missingFields ?? []);

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <AlertTitle>{t('ONBOARDING.banner.title')}</AlertTitle>
      {status.outstanding.map((step) => step.reason).join(' ')}
      {fields.length > 0 && (
        <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
          {t('ONBOARDING.missing')}:{' '}
          {fields
            .map((f) => t(`ONBOARDING.field.${f}`, { defaultValue: f }))
            .join(', ')}
        </Box>
      )}
      <Box sx={{ mt: 1 }}>
        <Button component={RouterLink} to="/onboarding" size="small" color="inherit" variant="outlined">
          {t('ONBOARDING.banner.action')}
        </Button>
      </Box>
    </Alert>
  );
}
